import {
  type CalendarFilters,
  type CalendarView,
  type CalendarZoom,
  defaultCalendarFilters,
  defaultCalendarSettings,
} from "@repo/api";
import type { CalendarSettings, CalendarSettingsPayload } from "@repo/api";
import { assertUnreachable, toggleRecordArrayValue } from "@repo/utils";
import { type ActionType, createAction, getType } from "typesafe-actions";

export interface TeamFilterSelection {
  kind: "team";
  teamId: number;
  assignedToTeam: boolean;
  deselectedMemberIds: string[];
}

export interface TeamCalendarState {
  selection?: TeamFilterSelection;
  emailId?: string[];
  saveSettings?: boolean;
}

// What still needs to be saved to the API/localStorage after an action.
export interface PendingSave {
  calendarSettings?: CalendarSettingsPayload;
  calendarFilters?: CalendarFilters;
  zoomWidth?: number;
}

export interface CalenderAppointmentsSearchFilters {
  tableView: CalendarView;
  selectedDate: Date;
  showAvailableServices: boolean;
  teamCalendar: TeamCalendarState;
  agendaCalendar: {
    emailId: string[] | undefined;
  };
  overviewCalendar: {
    emailId: string[] | undefined;
  };
  calendarSettings: CalendarSettings;
  calendarFilters: CalendarFilters;
  pendingSave?: PendingSave;
}

export const isTeamSelected = (
  tc: TeamCalendarState
): tc is TeamCalendarState & {
  selection: TeamFilterSelection;
} => tc.selection !== undefined;

const toCalendarSettingsPayload = ({
  zoomWidth: _zoomWidth,
  ...calendarSettings
}: CalendarSettings): CalendarSettingsPayload => calendarSettings;

export const AppointmentFilterDefault: CalenderAppointmentsSearchFilters = {
  tableView: "AGENDA",
  selectedDate: new Date(),
  showAvailableServices: false,
  teamCalendar: { emailId: undefined, selection: undefined },
  agendaCalendar: {
    emailId: undefined,
  },
  overviewCalendar: {
    emailId: undefined,
  },
  calendarSettings: defaultCalendarSettings,
  calendarFilters: defaultCalendarFilters,
};

export type AppointmentFilterActions = typeof appointmentFilterActions;
export const appointmentFilterActions = {
  setDate: createAction("SET_DATE")<{ from: Date }>(),
  setTeamCalendar: createAction("SET_TEAM_CALENDAR")<TeamCalendarState>(),
  setAgendaCalendar: createAction("SET_AGENDA_CALENDAR")<
    CalenderAppointmentsSearchFilters["agendaCalendar"]
  >(),
  setOverviewCalendar: createAction("SET_OVERVIEW_CALENDAR")<
    CalenderAppointmentsSearchFilters["overviewCalendar"]
  >(),
  setTableView: createAction("SET_TABLE_VIEW")<CalendarView>(),
  syncLoadedCalendarSettings: createAction("SYNC_LOADED_CALENDAR_SETTINGS")<{
    calendarSettings: CalendarSettings;
    teamCalendar?: TeamCalendarState;
  }>(),
  setCalendarSettings: createAction("SET_CALENDAR_SETTINGS")<
    CalenderAppointmentsSearchFilters["calendarSettings"]
  >(),
  setZoomWidth: createAction("SET_ZOOM_WIDTH")<number>(),
  setCalendarFilters: createAction("SET_CALENDAR_FILTERS")<
    CalenderAppointmentsSearchFilters["calendarFilters"]
  >(),
  syncLoadedCalendarFilters: createAction("SYNC_LOADED_CALENDAR_FILTERS")<
    CalenderAppointmentsSearchFilters["calendarFilters"]
  >(),
  showAvailableServices: createAction("SHOW_AVAILABLE_SERVICES")<boolean>(),
  setZoom: createAction(
    "SET_ZOOM",
    (zoom: CalendarZoom, zoomWidth: number) => ({ zoom, zoomWidth })
  )(),
  setFilteredTasks: createAction("SET_CALENDAR_FILTERED_TASKS")<string>(),
  setHideCanceledEvents: createAction(
    "SET_CALENDAR_HIDE_CANCELED_EVENTS"
  )<boolean>(),
  setHideCompletedEvents: createAction(
    "SET_CALENDAR_HIDE_COMPLETED_EVENTS"
  )<boolean>(),
  setHideScheduleBlocks: createAction(
    "SET_CALENDAR_HIDE_SCHEDULE_BLOCKS"
  )<boolean>(),
  setHideHiddenActivity: createAction(
    "SET_CALENDAR_HIDE_HIDDEN_ACTIVITY"
  )<boolean>(),
  setFilteredScheduleTypes: createAction(
    "SET_CALENDAR_FILTERED_SCHEDULE_TYPES"
  )<string>(),
};

export function appointmentFilterReducer(
  state: CalenderAppointmentsSearchFilters,
  action: ActionType<AppointmentFilterActions>
): CalenderAppointmentsSearchFilters {
  switch (action.type) {
    case getType(appointmentFilterActions.setDate):
      return {
        ...state,
        selectedDate: action.payload.from,
      };

    case getType(appointmentFilterActions.setTeamCalendar): {
      const newSettings = action.payload.selection
        ? {
            ...state.calendarSettings,
            selectedTeam: action.payload.selection.teamId,
            deselectedMembersByTeam: {
              ...state.calendarSettings.deselectedMembersByTeam,
              [action.payload.selection.teamId]:
                action.payload.selection.deselectedMemberIds,
            },
          }
        : { ...state.calendarSettings, selectedTeam: undefined };
      return {
        ...state,
        teamCalendar: action.payload,
        calendarSettings: newSettings,
        pendingSave:
          action.payload.saveSettings === false
            ? undefined
            : { calendarSettings: toCalendarSettingsPayload(newSettings) },
      };
    }

    case getType(appointmentFilterActions.syncLoadedCalendarSettings):
      return {
        ...state,
        calendarSettings: action.payload.calendarSettings,
        teamCalendar: action.payload.teamCalendar ?? state.teamCalendar,
      };

    case getType(appointmentFilterActions.setAgendaCalendar):
      return {
        ...state,
        agendaCalendar: action.payload,
      };
    case getType(appointmentFilterActions.setOverviewCalendar):
      return {
        ...state,
        overviewCalendar: action.payload,
      };

    case getType(appointmentFilterActions.setTableView):
      return {
        ...state,
        tableView: action.payload,
      };
    case getType(appointmentFilterActions.setCalendarSettings):
      return {
        ...state,
        calendarSettings: action.payload,
        pendingSave: {
          calendarSettings: toCalendarSettingsPayload(action.payload),
        },
      };
    case getType(appointmentFilterActions.setZoomWidth):
      return {
        ...state,
        calendarSettings: {
          ...state.calendarSettings,
          zoomWidth: action.payload,
        },
        pendingSave: { zoomWidth: action.payload },
      };
    case getType(appointmentFilterActions.setCalendarFilters):
      return {
        ...state,
        calendarFilters: action.payload,
        pendingSave: { calendarFilters: action.payload },
      };
    case getType(appointmentFilterActions.syncLoadedCalendarFilters):
      return {
        ...state,
        calendarFilters: {
          ...defaultCalendarFilters,
          ...action.payload,
        },
      };
    case getType(appointmentFilterActions.showAvailableServices):
      return {
        ...state,
        showAvailableServices: action.payload,
      };

    case getType(appointmentFilterActions.setZoom): {
      const newSettings = {
        ...state.calendarSettings,
        zoom: action.payload.zoom,
        zoomWidth: action.payload.zoomWidth,
      };
      return {
        ...state,
        calendarSettings: newSettings,
        pendingSave: {
          calendarSettings: toCalendarSettingsPayload(newSettings),
          zoomWidth: action.payload.zoomWidth,
        },
      };
    }

    case getType(appointmentFilterActions.setFilteredTasks): {
      const newFilters = {
        ...state.calendarFilters,
        filteredTasks:
          toggleRecordArrayValue(
            state.calendarFilters.filteredTasks,
            action.payload
          ) ?? [],
      };
      return {
        ...state,
        calendarFilters: newFilters,
        pendingSave: { calendarFilters: newFilters },
      };
    }

    case getType(appointmentFilterActions.setHideCanceledEvents): {
      const newFilters = {
        ...state.calendarFilters,
        hideCanceledEvents: action.payload,
      };
      return {
        ...state,
        calendarFilters: newFilters,
        pendingSave: { calendarFilters: newFilters },
      };
    }

    case getType(appointmentFilterActions.setHideCompletedEvents): {
      const newFilters = {
        ...state.calendarFilters,
        hideCompletedEvents: action.payload,
      };
      return {
        ...state,
        calendarFilters: newFilters,
        pendingSave: { calendarFilters: newFilters },
      };
    }

    case getType(appointmentFilterActions.setHideScheduleBlocks): {
      const newFilters = {
        ...state.calendarFilters,
        hideScheduleBlocks: action.payload,
      };
      return {
        ...state,
        calendarFilters: newFilters,
        pendingSave: { calendarFilters: newFilters },
      };
    }

    case getType(appointmentFilterActions.setHideHiddenActivity): {
      const newFilters = {
        ...state.calendarFilters,
        hideHiddenActivity: action.payload,
      };
      return {
        ...state,
        calendarFilters: newFilters,
        pendingSave: { calendarFilters: newFilters },
      };
    }

    case getType(appointmentFilterActions.setFilteredScheduleTypes): {
      const newFilters = {
        ...state.calendarFilters,
        filteredScheduleTypes:
          toggleRecordArrayValue(
            state.calendarFilters.filteredScheduleTypes ?? [],
            action.payload
          ) ?? [],
      };
      return {
        ...state,
        calendarFilters: newFilters,
        pendingSave: { calendarFilters: newFilters },
      };
    }
  }

  assertUnreachable(action);
}
